/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getQuestion } from '../../../services/assessment';
import {
  QuestionBox,
  TextInput,
  ContinueButton,
  RadioGroup,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalText,
  ModalButton,
} from '../../pages/generateQuestionTemplate/generateQuestion.style';
import { ErrorMessageComponent } from '../../../components/uielements/error';
import { IntlMessages } from '../../../helper/langHelper';
import { AdminLayout } from '../adminLayout';
import { CheckCircle } from 'lucide-react';
import LoadingScreen from '../../pages/generateQuestionTemplate/loadingScreen';
import { ContentBox, FileInfoRow, RadioOption } from './Clusterflowtesting.style';
import { ActionButton, FileActions, FileName } from '../clusters/Clusters.styles';
import { viewFile } from '../../../services/admin/files';
import { useMutation } from '@tanstack/react-query';
import { toastActions } from '../../../components/uielements/toast/toast';

// Constants for phase management
const PHASES = {
  INIT: 'INIT',
  QUESTION: 'QUESTION',
  COMPLETED: 'COMPLETED',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
};

const ClustersFlowTesting = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get cluster array from location state
  const clusterArray = location.state?.clusterArray || [];

  // Question flow states
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [showCompletedModal, setShowCompletedModal] = useState(false);

  // LIFO question code stack from clusters
  const [questionCodeStack, setQuestionCodeStack] = useState([]);

  // Current active question
  const [currentQuestion, setCurrentQuestion] = useState(null);
  // currentQuestion shape: { questionType, question, questionCode, options, nextQuestionCode, clusterData }

  // All submitted answers
  const [allAnswers, setAllAnswers] = useState([]);

  // Current phase
  const [currentPhase, setCurrentPhase] = useState(PHASES.INIT);

  // Progress tracking
  const [totalCodes, setTotalCodes] = useState(0);
  const [completedCodes, setCompletedCodes] = useState(0);

  // ─── Init ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    initializeFlow();
  }, [clusterArray]);

  // ─── Utilities ──────────────────────────────────────────────────────────────

  const handleError = useCallback((errorMessage, fallbackKey = 'generic.error') => {
    setError(<IntlMessages id={fallbackKey} defaultMessage={errorMessage} />);
    setLoading(false);
  }, []);

  const resetFormState = useCallback(() => {
    setAnswer('');
    setError('');
    setValidationError('');
  }, []);

  // ─── Initialize Flow ─────────────────────────────────────────────────────────

  const initializeFlow = async () => {
    setLoading(true);

    if (!clusterArray || clusterArray.length === 0) {
      handleError('No clusters available for testing.', 'clusters.not.found');
      return;
    }

    try {
      // Extract question codes from cluster array
      const questionCodes = clusterArray.map((cluster) => cluster.questionCode);

      if (questionCodes.length === 0) {
        setShowCompletedModal(true);
        setCurrentPhase(PHASES.NOT_SUPPORTED);
        setLoading(false);
        return;
      }

      setTotalCodes(questionCodes.length);

      // Initialize LIFO stack: pop last code for first question
      const lastCode = questionCodes[questionCodes.length - 1];
      const remainingStack = questionCodes.slice(0, questionCodes.length - 1);
      setQuestionCodeStack(remainingStack);

      // Start flow with the first question (last code in array - LIFO)
      await startFlowWithCode(lastCode, remainingStack, clusterArray);
    } catch (err) {
      console.error('Error initializing flow:', err);
      handleError('Error initializing flow. Please try again.', 'flow.init.error');
    } finally {
      setLoading(false);
    }
  };

  // ─── LIFO Flow: start a new flow with a question code ─────────────────────────

  /**
   * Calls getQuestion with only questionCode (no response) to start a new sub-flow.
   * stackSnapshot is passed in to avoid stale closure issues.
   */
  const startFlowWithCode = async (questionCode, stackSnapshot, clusterData) => {
    setLoading(true);
    resetFormState();

    try {
      const response = await getQuestion({ questionCode });

      await processApiResponse(response, stackSnapshot, clusterData);
    } catch (err) {
      console.error('Error starting flow with code:', questionCode, err);
      handleError('Error fetching question. Please try again.', 'question.fetch.error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Submit current answer ──────────────────────────────────────────────────

  const handleSubmit = async () => {
    setValidationError('');
    setError('');

    if (!answer || (typeof answer === 'string' && answer.trim() === '')) {
      setError(<IntlMessages id="answer.required" defaultMessage="Please provide an answer." />);
      return;
    }

    setLoading(true);

    try {
      const response = await getQuestion({
        questionCode: currentQuestion.questionCode,
        response: answer,
      });

      await processApiResponse(response, questionCodeStack, clusterArray);
    } catch (err) {
      console.error('Error submitting answer:', err);
      handleError('Error submitting answer. Please try again.', 'submit.error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Central response processor ─────────────────────────────────────────────

  /**
   * Handles any getQuestion() API response.
   * stackSnapshot: current in-memory code stack at the moment of the call.
   */
  const processApiResponse = async (response, stackSnapshot, clusterData) => {
    const { status, data } = response;

    if (status === 'DATA') {
      // Append any nextQuestionCode values to the stack
      let updatedStack = [...stackSnapshot];
      if (data.nextQuestionCode && data.nextQuestionCode.length > 0) {
        updatedStack = [...updatedStack, ...data.nextQuestionCode];
        setQuestionCodeStack(updatedStack);
      } else {
        setQuestionCodeStack(updatedStack);
      }

      // Cache the answer for the previous question if there was one
      if (currentQuestion && answer) {
        setAllAnswers((prev) => [
          ...prev,
          {
            question: currentQuestion.question,
            answer: answer,
            questionCode: currentQuestion.questionCode,
            clusterName: currentQuestion.clusterData?.name,
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      // Show new question
      setCurrentQuestion(data);
      setCurrentPhase(PHASES.QUESTION);
      resetFormState();
    } else if (status === 'VALIDATION') {
      // Keep the same question, show validation error, preserve user's typed answer
      setValidationError(data || 'Invalid response. Please check your answer and try again.');
    } else if (status === 'TERMINATE') {
      // Cache the final answer before terminating
      if (currentQuestion && answer) {
        setAllAnswers((prev) => [
          ...prev,
          {
            question: currentQuestion.question,
            answer: answer,
            questionCode: currentQuestion.questionCode,
            clusterName: currentQuestion.clusterData?.name,
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      // Check if there are more codes in the LIFO stack
      if (stackSnapshot && stackSnapshot.length > 0) {
        // Pop last code and start a new sub-flow
        const lastCode = stackSnapshot[stackSnapshot.length - 1];
        const remainingStack = stackSnapshot.slice(0, stackSnapshot.length - 1);
        setQuestionCodeStack(remainingStack);
        setCurrentQuestion(null);
        resetFormState();
        setCompletedCodes((prev) => prev + 1);
        await startFlowWithCode(lastCode, remainingStack, clusterData);
      } else {
        // All flows complete — show completion modal
        setCurrentQuestion(null);
        setCurrentPhase(PHASES.COMPLETED);
        setShowCompletedModal(true);
      }
    }
  };

  // File view mutation
  const fileViewMutation = useMutation({
    mutationFn: (payload) => viewFile(payload),
    onSuccess: (response) => {
      if (response) {
        const url = response?.documentUrl;
        window.open(url, '_blank');
      }
    },
    onError: () => {
      toastActions.error(
        <IntlMessages id="toast.file.view.error" defaultMessage="Failed to view file." />
      );
    },
  });

  const handleFileView = (documentId) => {
    fileViewMutation.mutate({ documentId });
  };

  // ─── Navigation ─────────────────────────────────────────────────────────────

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleCloseModal = () => {
    navigate(-1);
  };

  // ─── Render Answer Input ─────────────────────────────────────────────────────

  const renderAnswerInput = () => {
    if (!currentQuestion) return null;

    const { questionType, options } = currentQuestion;

    if (questionType === 'OPTION') {
      return (
        <RadioGroup>
          {options.map((option) => (
            <RadioOption key={option} checked={answer === option}>
              <input
                type="radio"
                name="answer"
                value={option}
                checked={answer === option}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={loading}
              />
              <span>{option}</span>
            </RadioOption>
          ))}
        </RadioGroup>
      );
    }

    if (questionType === 'NUMBER') {
      return (
        <TextInput
          id="answer-input"
          type="number"
          min="0"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Enter a number..."
          aria-labelledby="answer-label"
          aria-required="true"
          disabled={loading}
          style={{ background: 'transparent' }}
        />
      );
    }

    // TEXT (default)
    return (
      <TextInput
        id="answer-input"
        type="text"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="Enter your answer..."
        aria-labelledby="answer-label"
        aria-required="true"
        disabled={loading}
        style={{ background: 'transparent' }}
      />
    );
  };

  // ─── Render Current Question ────────────────────────────────────────────────

  const renderCurrentQuestion = () => {
    if (!currentQuestion) return null;

    return (
      <>
        <QuestionBox role="heading" aria-level="1">
          {currentQuestion.clusterData?.name}
        </QuestionBox>

        <QuestionBox role="heading" aria-level="2">
          {currentQuestion.question}
        </QuestionBox>

        {validationError && (
          <ErrorMessageComponent message={validationError} role="alert" aria-live="assertive" />
        )}

        <div role="form">
          <label id="answer-label" htmlFor="answer-input" className="visually-hidden">
            Answer
          </label>
          {renderAnswerInput()}
        </div>

        {currentQuestion.documentArray &&
          currentQuestion.documentArray.length > 0 &&
          currentQuestion.documentArray.map((doc) => (
            <FileInfoRow key={doc._id}>
              <FileName>✅ {doc?.documentId}</FileName>
              <FileActions>
                <ActionButton
                  type="button"
                  onClick={() => handleFileView(doc?._id)}
                  disabled={fileViewMutation.isPending}
                >
                  View
                </ActionButton>
              </FileActions>
            </FileInfoRow>
          ))}

        <ContinueButton
          onClick={handleSubmit}
          disabled={!answer || loading}
          aria-disabled={!answer || loading}
        >
          {loading ? 'Loading...' : 'Next'}
        </ContinueButton>
      </>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <AdminLayout
      pageTitle="Clusters Flow Testing"
      activeTab="clustersFlowTesting"
      rightHeader={
        !loading &&
        !error &&
        currentQuestion && (
          <div
            style={{
              padding: '16px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px',
            }}
          >
            Question {completedCodes + 1} of {totalCodes}
          </div>
        )
      }
    >
      {showCompletedModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <CheckCircle size={32} color="#16a34a" />
              <ModalTitle>Assessment Complete</ModalTitle>
            </ModalHeader>
            <ModalText>
              You have successfully completed all questions in the assessment flow. Your answers
              have been recorded.
            </ModalText>
            <ModalButton onClick={handleCloseModal}>Back to Flows</ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}

      {error ? (
        <ContentBox>
          <ErrorMessageComponent message={error} role="alert" aria-live="assertive" />
          <ContinueButton onClick={handleBackClick} style={{ marginTop: '16px' }}>
            Back
          </ContinueButton>
        </ContentBox>
      ) : (
        <ContentBox role="region" aria-label="Question Form" className="hide-scroll-bar">
          {loading ? <LoadingScreen /> : renderCurrentQuestion()}
        </ContentBox>
      )}
    </AdminLayout>
  );
};

export default ClustersFlowTesting;
